import { PlusOutlined } from '@ant-design/icons';
import { Button, Col, Input, message, Row, Typography, Upload } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { cnpj } from 'cpf-cnpj-validator';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import InputColor, { Color } from 'react-input-color';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import CNPJInput from '../../components/CNPJInput';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import { MAX_SIZE_2MB } from '../../consts';
import { withRouteChange } from '../../services/APM';

const mimeTypes = {
  jpg: 'image/jpeg',
  png: 'image/png',
  jpeg: 'image/jpeg',
};

function fileTypeToMimeType(extension: string): string | undefined {
  return mimeTypes[extension as keyof typeof mimeTypes];
}

const supportedFiles = ['.jpg', '.png', '.jpeg', 'image/jpg', 'image/png', 'image/jpeg'];
interface CompanyInfoFormValues {
  businessName: string;
  companyName: string;
  companyDescription: string;
  cnpj: string;
  companyURL: string;
  companyFromName: string;
  companyLogo?: UploadFile[];
  companyLogoPreview?: UploadFile[];
  buttonColor?: string;
  textColor?: string;
}

const CompanyInfo = () => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const companyLogoValue = Form.useWatch('companyLogo', form);
  const companyLogoPreviewValue = Form.useWatch('companyLogoPreview', form);
  const buttonColorValue = Form.useWatch('buttonColor', form);
  const textColorValue = Form.useWatch('textColor', form);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const mutation = useAPIMutation(
    async (
      {
        businessName,
        companyName,
        companyDescription,
        cnpj,
        companyURL,
        companyLogo,
        companyLogoPreview,
        companyFromName,
        buttonColor,
        textColor,
      }: CompanyInfoFormValues,
      api
    ) => {
      const config = await api.get('/configs/tenant-configurations/');

      if (companyLogo?.length) {
        if (!companyLogo[0].url) {
          const formData = new FormData();
          formData.append('value', companyLogo[0].originFileObj as File);

          await api.patch('/configs/tenant-configurations/company-logo/', formData, {
            maxBodyLength: MAX_SIZE_2MB,
          });
        }
      }

      if (companyLogoPreview?.length) {
        if (!companyLogoPreview[0].url) {
          const formData = new FormData();
          formData.append('value', companyLogoPreview[0].originFileObj as File);

          await api.patch('/configs/tenant-configurations/company-logo-preview/', formData, {
            maxBodyLength: MAX_SIZE_2MB,
          });
        }
      }

      if (config.data.companyWelcomeMessageTemplateId) {
        const activeWelcomeTemplate = await api.get(
          `/email-communication-templates/${config.data.companyWelcomeMessageTemplateId}/`
        );
        if (!activeWelcomeTemplate.data?.from_name) {
          await api.patch(
            `/email-communication-templates/${config.data.companyWelcomeMessageTemplateId}/`,
            {
              from_name: companyFromName,
            }
          );
        }
      }

      return await Promise.all([
        api.patch('/configs/tenant-configurations/business-name/', {
          value: businessName,
        }),
        api.patch('/configs/tenant-configurations/company-name/', {
          value: companyName,
        }),
        api.patch('/configs/tenant-configurations/company-description/', {
          value: companyDescription,
        }),
        api.patch('/configs/tenant-configurations/company-url/', {
          value: companyURL,
        }),
        api.patch('/configs/tenant-configurations/company-cnpj/', {
          value: cnpj,
        }),
        api.patch('/configs/tenant-configurations/company-from-name/', {
          value: companyFromName,
        }),
        api.patch('/configs/tenant-configurations/career-page-button-color/', {
          value: buttonColor,
        }),
        api.patch('/configs/tenant-configurations/career-page-button-text-color/', {
          value: textColor,
        }),
      ]);
    },
    {
      onSuccess: () => {
        message.success(t('Configurações atualizadas.'));
      },
      onSettled: async () => {
        await queryClient.invalidateQueries('/configs/tenant-configurations/');
      },
    }
  );

  return (
    <ErrorResult error={config.error}>
      <Helmet>
        <title>
          {t('Dados da Empresa | Configurações')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <Form<CompanyInfoFormValues>
        form={form}
        layout="vertical"
        initialValues={{
          businessName: config.data?.businessName,
          companyName: config.data?.companyName,
          companyDescription: config.data?.companyDescription,
          companyURL: config.data?.companySiteUrl,
          cnpj: config.data?.companyCNPJ,
          companyFromName: config.data?.companyFromName,
          companyLogo: config.data?.companyLogoUrl
            ? [
                {
                  status: 'done',
                  url: `${window.location.origin}${config.data?.companyLogoUrl}`,
                  name: 'Logomarca',
                },
              ]
            : [],
          companyLogoPreview: config.data?.companyLogoPreviewUrl
            ? [
                {
                  status: 'done',
                  url: `${window.location.origin}${config.data?.companyLogoPreviewUrl}`,
                  name: 'Logotipo',
                },
              ]
            : [],
          buttonColor: config.data?.careerPageButtonColor || '#1890ffff',
          textColor: config.data?.careerPageButtonTextColor || '#ffffffff',
        }}
        onFinish={(values) => {
          mutation.mutate(values);
        }}
      >
        <Form.Row>
          <Form.Col span={12}>
            <Form.Legend>
              <Trans>Dados da Empresa</Trans>
            </Form.Legend>
            <Form.Item
              name="businessName"
              label={t('Nome Fantasia')}
              rules={[
                {
                  message: t('Campo obrigatório'),
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="companyName" label={t('Razão Social')}>
              <Input />
            </Form.Item>
            <Form.Item
              name="cnpj"
              label={t('CNPJ')}
              rules={[
                {
                  validator(_, value: string) {
                    if (!value || value === '' || cnpj.isValid(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('Insira um CNPJ válido')));
                  },
                },
              ]}
            >
              <CNPJInput />
            </Form.Item>
            <Form.Item name="companyURL" label={t('Site da empresa')} rules={[{ type: 'url' }]}>
              <Input />
            </Form.Item>
            <Form.Item
              name="companyFromName"
              label={t('Nome público para envio de e-mail')}
              tooltip={{
                title: (
                  <Typography.Text style={{ color: '#fff' }}>
                    <Trans>
                      Esse título será utilizado no campo "Enviado por" nas comunicações via e-mail
                      com os candidatos.
                    </Trans>
                    <br />
                    <Trans>
                      Você também poderá editá-lo no momento da criação dos modelos ou envio de
                      e-mails.
                    </Trans>
                  </Typography.Text>
                ),
                placement: 'bottom',
              }}
              rules={[
                {
                  message: t('Campo obrigatório'),
                  required: true,
                },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              name="companyDescription"
              label={t('Descrição da empresa')}
              tooltip={{
                title: (
                  <Typography.Text style={{ color: '#fff' }}>
                    <Trans>
                      Forneça um resumo estratégico da sua empresa para melhorar a personalização
                      das funcionalidades do sistema. Inclua setor de atuação, principais
                      produtos/serviços, porte, cultura e marcos importantes.
                    </Trans>
                  </Typography.Text>
                ),
                placement: 'top',
              }}
              rules={[
                {
                  message: t('Campo obrigatório'),
                  required: true,
                },
              ]}
            >
              <Input.TextArea rows={6} maxLength={1000} showCount />
            </Form.Item>
          </Form.Col>
          <Form.Col span={12}>
            <Form.Legend>
              <Trans>Página de Carreiras</Trans>
            </Form.Legend>
            <Row align="middle">
              <Col>
                <Form.Item
                  label={t('Logomarca')}
                  name="companyLogo"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => {
                    if (Array.isArray(e)) {
                      return e;
                    }
                    return e && e.fileList;
                  }}
                >
                  <Upload
                    maxCount={1}
                    listType="picture-card"
                    accept={supportedFiles.join(',')}
                    showUploadList={{ showRemoveIcon: false }}
                    beforeUpload={(file: UploadFile) => {
                      if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                        if (!file.size) {
                          //O que significa não ter size?
                          message.error(t('Erro com o arquivo.'));
                          return Upload.LIST_IGNORE;
                        }
                        if (file.size >= MAX_SIZE_2MB) {
                          message.error(
                            t('Por favor, selecione um arquivo de até 2Mb para upload.')
                          );
                          return Upload.LIST_IGNORE;
                        }

                        return false;
                      } else {
                        message.error(
                          t('Por favor, selecione um arquivo no formato ".png", ".jpg", ".jpeg".')
                        );
                        return Upload.LIST_IGNORE;
                      }
                    }}
                  >
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>
                        {companyLogoValue?.length >= 1 ? t('Alterar Logo') : 'Upload'}
                      </div>
                    </div>
                  </Upload>
                </Form.Item>
              </Col>
              <Col>
                <Form.Item label={<></>}>
                  <Typography.Text type="secondary">
                    <Trans>Visível na Página de Carreiras</Trans>
                  </Typography.Text>
                  <br />
                  <Typography.Text type="secondary">
                    <Trans>(Resolução recomendada: 600px X 400px)</Trans>
                  </Typography.Text>
                </Form.Item>
              </Col>
            </Row>
            <Row align="middle">
              <Col>
                <Form.Item
                  label={t('Logotipo')}
                  name="companyLogoPreview"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => {
                    if (Array.isArray(e)) {
                      return e;
                    }
                    return e && e.fileList;
                  }}
                >
                  <Upload
                    maxCount={1}
                    listType="picture-card"
                    accept={supportedFiles.join(',')}
                    showUploadList={{ showRemoveIcon: false }}
                    beforeUpload={(file: UploadFile) => {
                      if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                        if (!file.size) {
                          //O que significa não ter size?
                          message.error(t('Erro com o arquivo.'));
                          return Upload.LIST_IGNORE;
                        }
                        if (file.size >= MAX_SIZE_2MB) {
                          message.error(
                            t('Por favor, selecione um arquivo de até 2Mb para upload.')
                          );
                          return Upload.LIST_IGNORE;
                        }

                        return false;
                      } else {
                        message.error(
                          t('Por favor, selecione um arquivo no formato ".png", ".jpg", ".jpeg".')
                        );
                        return Upload.LIST_IGNORE;
                      }
                    }}
                  >
                    <div>
                      <PlusOutlined />
                      <div style={{ marginTop: 8 }}>
                        {companyLogoPreviewValue?.length >= 1 ? t('Alterar Logo') : 'Upload'}
                      </div>
                    </div>
                  </Upload>
                </Form.Item>
              </Col>
              <Col>
                <Form.Item label={<></>}>
                  <Typography.Text type="secondary">
                    <Trans>Visível nos Job Boards</Trans>
                  </Typography.Text>
                  <br />
                  <Typography.Text type="secondary">
                    <Trans>(Resolução recomendada: 200px X 200px)</Trans>
                  </Typography.Text>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label={t('Cor dos botões')}>
              <Row gutter={8}>
                <Col>
                  <InputColor
                    initialValue={buttonColorValue || '#1890ffff'}
                    onChange={(value: Color) => form.setFieldValue('buttonColor', value.hex)}
                    placement="right"
                  />
                </Col>
                <Col span={5}>
                  <Form.Item name="buttonColor" noStyle>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Typography.Text type="secondary">
                    <Trans>
                      Essa cor será aplicada no preenchimento dos botões da Página de Carreiras
                    </Trans>
                  </Typography.Text>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item label={t('Cor do texto')}>
              <Row gutter={8}>
                <Col>
                  <InputColor
                    initialValue={textColorValue || '#ffffffff'}
                    onChange={(value: Color) => form.setFieldValue('textColor', value.hex)}
                    placement="right"
                  />
                </Col>
                <Col span={5}>
                  <Form.Item name="textColor" noStyle>
                    <Input />
                  </Form.Item>
                </Col>
                <Col span={16}>
                  <Typography.Text type="secondary">
                    <Trans>
                      Essa cor será aplicada aos textos contidos nos botões da Página de Carreiras
                    </Trans>
                  </Typography.Text>
                </Col>
              </Row>
            </Form.Item>
          </Form.Col>
        </Form.Row>
        <Form.Footer>
          <Form.FooterEnd>
            <Button onClick={() => form.resetFields()}>
              <Trans>Cancelar</Trans>
            </Button>
            <Button type="primary" htmlType="submit" loading={mutation.isLoading}>
              <Trans>Salvar</Trans>
            </Button>
          </Form.FooterEnd>
        </Form.Footer>
      </Form>
    </ErrorResult>
  );
};

export default withRouteChange()(CompanyInfo);
